import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { VideoAnalysisProvider, VideoListItem } from '@sr-test-wizard/shared';
import { useVideosStore } from '../../state/videosStore';
import { VideoUploadZone } from './VideoUploadZone';
import { VideoPlayer } from './VideoPlayer';
import { VideoAnalysisList } from './VideoAnalysisList';

const LEFT_WIDTH_KEY = 'sneaky:videos:leftWidth';
const LEFT_COLLAPSED_KEY = 'sneaky:videos:leftCollapsed';
const MIN_W = 180;
const MAX_W = 480;
const DEFAULT_W = 260;

/**
 * Compact one-line progress readout for the selected video's current
 * extract / analyze job. Designed to sit inline on the filename header row
 * so it replaces the unused "duration unknown" label and frees the space
 * below the video that the previous card-style banner used to occupy.
 * Returns `null` when no job is running for this slug.
 */
function InlineProgress({ slug, provider }: { slug: string; provider: VideoAnalysisProvider }) {
  const progress = useVideosStore((s) => s.progressByKey[`${slug}::${provider}`]);
  const extract = useVideosStore((s) => s.progressByKey[`${slug}::extract`]);
  const nativeStage = useVideosStore((s) => s.nativeStageByKey[`${slug}::${provider}`]);
  const shown = progress ?? extract;
  if (!shown) {
    if (!nativeStage) return null;
    return (
      <span className="small muted">
        Gemini {nativeStage.stage}: {nativeStage.status}
      </span>
    );
  }
  const { kind, current, total, completed, errors, status } = shown;
  const totalLabel = total ?? '?';
  const stageLabel =
    kind === 'extract'
      ? provider === 'gemini-native'
        ? 'Extracting thumbnails'
        : 'Extracting frames'
      : provider === 'gemini-native'
        ? 'Running video analysis'
        : 'Analyzing frames';
  const badgeClass =
    status === 'failed' ? 'bad' : status === 'completed' ? 'good' : '';
  return (
    <span
      className="small"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
      }}
      title={shown.message ?? undefined}
    >
      <strong>{stageLabel}</strong>
      <span className="muted">
        {current}/{totalLabel}
        {kind === 'analyze' && ` - ${completed} ok - ${errors} errors`}
      </span>
      <span className={`badge ${badgeClass}`}>{status}</span>
    </span>
  );
}

/**
 * One card per video in the narrow left column. Click anywhere on the card
 * body to select it; the action buttons stop propagation so they don't also
 * trigger selection. Labels stay short ("Re-analyse", "Force") so the card
 * stays usable even at the minimum column width.
 */
function VideoListRow({
  item,
  provider,
  selected,
  onSelect,
  onExtract,
  onAnalyze,
  onDelete,
}: {
  item: VideoListItem;
  provider: VideoAnalysisProvider;
  selected: boolean;
  onSelect: () => void;
  onExtract: () => void;
  onAnalyze: (force: boolean) => void;
  onDelete: () => void;
}) {
  const isGemini = provider === 'gemini-native';
  const busy = item.extractInProgress || item.analyzeInProgress;
  return (
    <div
      className={`card small${selected ? ' is-selected' : ''}`}
      style={{
        marginBottom: 6,
        cursor: 'pointer',
        borderColor: selected ? 'var(--brand)' : 'var(--border)',
      }}
      onClick={onSelect}
    >
      <strong
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={item.videoFile}
      >
        {item.videoFile}
      </strong>
      <div className="row small" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
        {item.frameCount > 0 && <span className="badge">{item.frameCount}f</span>}
        {item.analysisByProvider?.['openai-frames'] && <span className="badge good">OA</span>}
        {item.analysisByProvider?.['gemini-native'] && <span className="badge good">GM</span>}
        {item.extractInProgress && <span className="badge">extracting thumbnails...</span>}
        {item.analyzeInProgress && (
          <span className="badge">
            {isGemini ? 'running video analysis...' : 'analysing frames...'}
          </span>
        )}
      </div>
      <div className="row" style={{ marginTop: 6, gap: 4, flexWrap: 'wrap' }}>
        <button
          className="btn small"
          onClick={(e) => {
            e.stopPropagation();
            onExtract();
          }}
          disabled={busy}
          title={
            isGemini
              ? 'Optional: extract thumbnails for frame zoom and evidence range modal.'
              : 'Extract 1-fps frames for OpenAI frame analysis.'
          }
        >
          {item.frameCount > 0
            ? isGemini
              ? 'Re-extract thumbs'
              : 'Re-extract'
            : isGemini
              ? 'Extract thumbs'
              : 'Extract'}
        </button>
        <button
          className="btn btn-primary small"
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze(false);
          }}
          disabled={busy || (!isGemini && item.frameCount === 0)}
        >
          {item.analysisByProvider?.[provider]
            ? isGemini
              ? 'Re-run video analysis'
              : 'Re-analyse'
            : isGemini
              ? 'Execute video analysis'
              : 'Analyse'}
        </button>
        <button
          className="btn btn-ghost small"
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze(true);
          }}
          disabled={busy || (!isGemini && item.frameCount === 0)}
          title={
            isGemini
              ? 'Ignore cache and re-run native video analysis.'
              : 'Re-run every frame through the vision model.'
          }
        >
          {isGemini ? 'Force re-run' : 'Force'}
        </button>
        <span className="spacer" />
        <button
          className="btn btn-ghost small"
          onClick={(e) => {
            e.stopPropagation();
            if (
              confirm(
                `Delete ${item.videoFile}? This removes the video, frames and analysis.`,
              )
            ) {
              onDelete();
            }
          }}
          disabled={busy}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/**
 * Extras-tool page that owns the Gameplay Video Analyzer flow:
 *   - Upload videos into `<runRoot>/videos/`
 *   - Trigger 1-fps frame extraction (ffmpeg)
 *   - Trigger per-frame OpenAI vision analysis
 *   - Play the selected video and scroll the synchronised analysis list
 *
 * Layout: a small title card on top, a two-column row with a narrow videos
 * column on the left (collapsible via chevron) and the player on the right,
 * separated by a draggable vertical splitter. The per-frame analysis card
 * spans the full width below. The column width and collapsed state are
 * persisted in localStorage so the user's preference sticks across reloads.
 */
export function VideoAnalyzerPage() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const list = useVideosStore((s) => s.list);
  const listLoading = useVideosStore((s) => s.listLoading);
  const error = useVideosStore((s) => s.error);
  const selectedSlug = useVideosStore((s) => s.selectedSlug);
  const selectedProvider = useVideosStore((s) => s.selectedProvider);
  const analysisByKey = useVideosStore((s) => s.analysisByKey);
  const load = useVideosStore((s) => s.load);
  const select = useVideosStore((s) => s.select);
  const setProvider = useVideosStore((s) => s.setProvider);
  const startExtract = useVideosStore((s) => s.startExtract);
  const startAnalyze = useVideosStore((s) => s.startAnalyze);
  const remove = useVideosStore((s) => s.remove);

  const [leftWidth, setLeftWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return DEFAULT_W;
    const raw = Number(window.localStorage.getItem(LEFT_WIDTH_KEY));
    return Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W ? raw : DEFAULT_W;
  });
  const [leftCollapsed, setLeftCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(LEFT_COLLAPSED_KEY) === '1';
  });

  useEffect(() => {
    void load(runId);
  }, [runId, load]);

  useEffect(() => {
    window.localStorage.setItem(LEFT_WIDTH_KEY, String(leftWidth));
  }, [leftWidth]);
  useEffect(() => {
    window.localStorage.setItem(LEFT_COLLAPSED_KEY, leftCollapsed ? '1' : '0');
  }, [leftCollapsed]);

  const selected = list.find((v) => v.slug === selectedSlug) ?? null;
  const analysis = selectedSlug
    ? analysisByKey[`${selectedSlug}::${selectedProvider}`] ?? null
    : null;
  const hasVideos = list.length > 0;
  const isGemini = selectedProvider === 'gemini-native';

  const onSplitterMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const move = (m: MouseEvent) => {
      const next = Math.max(MIN_W, Math.min(MAX_W, startW + (m.clientX - startX)));
      setLeftWidth(next);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!leftCollapsed && (
        <div
          className="row"
          style={{
            gap: 10,
            alignItems: 'baseline',
            flexWrap: 'wrap',
            padding: '0 2px',
          }}
        >
          <strong style={{ fontSize: 14 }}>Gameplay Video Analyzer</strong>
          <span
            className="muted small"
            style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}
            title={
              isGemini
                ? 'Native video analysis path. Thumbnail extraction is optional and used only for frame zoom/evidence UX.'
                : 'Frame-by-frame path. Extract 1-fps frames then run OpenAI vision per frame.'
            }
          >
            {isGemini
              ? 'Native video analysis (Gemini). Optional thumbnail extraction only for UI evidence previews.'
              : 'Frame-by-frame analysis (OpenAI). Extract 1-fps frames then analyze each frame.'}
          </span>
          <select
            className="input"
            value={selectedProvider}
            onChange={(e) => setProvider(e.target.value as VideoAnalysisProvider)}
            style={{ width: 170 }}
            title="Select analysis provider"
          >
            <option value="gemini-native">Gemini Native (recommended)</option>
            <option value="openai-frames">OpenAI Frames (legacy)</option>
          </select>
        </div>
      )}

      {error && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          <span style={{ color: 'var(--bad)' }} className="small">
            {error}
          </span>
        </div>
      )}

      <div
        className="row"
        style={{ alignItems: 'stretch', gap: 0, flexWrap: 'nowrap' }}
      >
        {leftCollapsed ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setLeftCollapsed(false)}
            title="Expand videos list"
            style={{
              flex: '0 0 28px',
              padding: '8px 0',
              writingMode: 'vertical-rl',
              borderRadius: 6,
              marginRight: 6,
            }}
          >
            {'\u00BB'} Videos ({list.length})
          </button>
        ) : (
          <>
            <div
              className="card"
              style={{
                width: leftWidth,
                flex: '0 0 auto',
                minWidth: MIN_W,
                maxWidth: MAX_W,
                marginRight: 0,
              }}
            >
              <div className="row" style={{ marginBottom: 8, gap: 6 }}>
                <button
                  type="button"
                  className="btn btn-ghost small"
                  onClick={() => setLeftCollapsed(true)}
                  title="Collapse list"
                  style={{ padding: '0 6px' }}
                >
                  {'\u00AB'}
                </button>
                <strong style={{ flex: 1 }}>
                  Videos {listLoading ? '...' : `(${list.length})`}
                </strong>
              </div>
              <div style={{ marginBottom: 8 }}>
                <VideoUploadZone compact />
              </div>
              {hasVideos ? (
                list.map((item) => (
                  <VideoListRow
                    key={item.slug}
                    item={item}
                    provider={selectedProvider}
                    selected={item.slug === selectedSlug}
                    onSelect={() => void select(item.slug)}
                    onExtract={() => void startExtract(item.slug)}
                    onAnalyze={(force) => void startAnalyze(item.slug, force, selectedProvider)}
                    onDelete={() => void remove(item.slug)}
                  />
                ))
              ) : (
                <p className="muted small" style={{ margin: 0 }}>
                  No local videos yet. Import from Drive above or drop a file on the right.
                </p>
              )}
            </div>

            <div
              role="separator"
              aria-orientation="vertical"
              onMouseDown={onSplitterMouseDown}
              title="Drag to resize"
              style={{
                flex: '0 0 6px',
                cursor: 'col-resize',
                background: 'var(--border)',
                borderRadius: 3,
                margin: '0 4px',
              }}
            />
          </>
        )}

        <div
          className="card"
          style={{ flex: 1, minWidth: 0, padding: 8 }}
        >
          {selected ? (
            <>
              <div
                className="row"
                style={{ marginBottom: 4, gap: 8, alignItems: 'center' }}
              >
                <span
                  className="small"
                  style={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                  title={selected.videoFile}
                >
                  {selected.videoFile}
                </span>
                <InlineProgress slug={selected.slug} provider={selectedProvider} />
              </div>
              <VideoPlayer key={selected.slug} runId={runId} slug={selected.slug} />
            </>
          ) : !hasVideos ? (
            <VideoUploadZone />
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Pick a video on the left.
            </p>
          )}
        </div>
      </div>

      {selected && (
        <div className="card" style={{ padding: 8 }}>
          {analysis ? (
            <VideoAnalysisList
              runId={runId}
              slug={selected.slug}
              analysis={analysis}
              title={isGemini ? 'Video analysis timeline' : 'Per-frame analysis'}
            />
          ) : (
            <>
              <strong className="small" style={{ display: 'block', marginBottom: 6 }}>
                {isGemini ? 'Video analysis timeline' : 'Per-frame analysis'}
              </strong>
              <p className="muted small" style={{ margin: 0 }}>
                {selected.analysisExists
                  ? selected.analysisByProvider?.[selectedProvider]
                    ? 'Loading analysis...'
                    : `No ${selectedProvider === 'gemini-native' ? 'Gemini Native' : 'OpenAI Frames'} analysis yet. Click "${isGemini ? 'Execute video analysis' : 'Analyse'}" above.`
                  : isGemini
                    ? 'Click "Execute video analysis" to run native video understanding. "Extract thumbs" is optional for zoom thumbnails.'
                    : selected.frameCount > 0
                      ? 'Frames extracted - click "Analyse" above to run the vision model.'
                      : 'Run "Extract frames" then "Analyse" to populate this section.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
